import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useFocusEffect, usePathname, useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import { CardType, CardTypes, IRevaultInformation } from 'features/payment/types';
import {
  isCashApp,
  isPayPal,
  isVenmo,
  mapCardTypeToLoyalty,
  mapPaymentMethodCardTypes,
  shouldReVault,
} from 'features/payment/utils';
import { useStoreContext } from 'features/store';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { getStoreLocalTime } from 'features/store/utils';
import { useIntl } from 'react-intl';
import { GestureResponderEvent, View } from 'react-native';

import { CardModal } from 'components/card-modal';
import { CheckoutErrorsModal } from 'components/checkout-errors-modal';
import CreditCardFormInputs from 'components/credit-card-form-inputs';
import PaymentMethod from 'components/payment-method';
import { cardIconRenderer } from 'components/payment-method-option/credit-card-icon';
import { isCreditCardPaymentMethod } from 'components/payment-method-option/utils';
import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { getCurrentSelectedMethodType } from 'components/payment-method/utils';
import { PaymentProcessLoading } from 'components/payment-process-loading';
import { CashAppButton } from 'components/payments/integrations/firstdata/cashapp/cashapp-button';
import PayPalButton from 'components/payments/integrations/firstdata/paypal-button';
import { PayPalVenmoExtraDisplay } from 'components/payments/integrations/firstdata/paypal-venmo-extra-display';
import { IBraintreeResult, ICashAppResult } from 'components/payments/integrations/firstdata/types';
import {
  getQueryParams,
  removeAllQueryParams,
} from 'components/payments/integrations/firstdata/utils';
import VenmoButton from 'components/payments/integrations/firstdata/venmo-button';
import { SafetechEncryption } from 'components/payments/integrations/orbital/components/encryption';
import {
  IEncryptionError,
  IEncryptionResult,
} from 'components/payments/integrations/orbital/components/encryption/types';
import WarningMessage from 'components/warning-message';
import { usePaymentCheckoutRedesignExperiment } from 'experiments/use-payment-checkout-redesign-experiment';
import { CartPaymentCardType, CartPaymentType, ServiceMode } from 'generated/graphql-gateway';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { usePrepaidsPaymentMethods } from 'hooks/prepaid/use-prepaids-payment-methods';
import { useCart } from 'hooks/use-cart';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import useIsSignUpAfterCart from 'hooks/use-is-sign-up-after-cart';
import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import { DeliveryCancellationPolicy } from 'pages/cart/your-cart/delivery-cancellation-policy';
import Totals from 'pages/cart/your-cart/totals';
import TipSelectorRedesign from 'pages/cart/your-cart/totals/tip-selector-redesign';
import { useTotalTipCalculations } from 'pages/cart/your-cart/totals/use-total-tip-calculations';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { useCartContext } from 'state/cart';
import { selectors, useAppSelector } from 'state/global-state';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  OrderInjectionFlowVariations,
  PaymentFieldVariations,
  SupportedCardType,
  defaultPaymentFieldVariation,
  defaultSupportedCardTypes,
} from 'state/launchdarkly/variations';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { getServerOrderSubtotalBeforeOfferDiscounts } from 'state/loyalty/hooks/utils/helpers';
import { Region } from 'utils/environment/types';
import logger from 'utils/logger';
import { routes } from 'utils/routing';
import { getLocalizedServiceMode, isDelivery } from 'utils/service-mode';

import { PlaceOrderButton } from '../../place-order-button';
import SectionHeading from '../../section-heading';
import { DeliveryCancellationRedesign } from '../delivery-cancellation-redesign';
import { getOrderProcessingText } from '../get-order-processing-text';
import PrepaidMethodReload from '../prepaid-method-reload';
import PrepaidTransferBalance from '../prepaid-transfer-balance';
import {
  DisclaimerWrapper,
  ErrorContainer,
  ErrorMessage,
  IconContainer,
  PaymentSelection,
  WarningContainer,
} from '../styled';
import { logCartContinueOrder } from '../utils';

import { Totals as OrderPaymentTotals } from './Totals';
import { isRbiOrderExpired } from './is-rbi-order-id-expired';
import { PaymentFooter } from './payment-footer';
import { PaymentMethodSelector } from './payment-method-selector-redesign/payment-method-selector';
import { PaymentRedesign } from './payment-redesign/payment-redesign';
import { AvailablePayments, PaymentMethodsContainer } from './payment-redesign/styled';
import { useOrderPayment } from './use-order-payment';
import { getNativePaymentDisclaimer } from './util';

function useRbiOrderIdExpirationCheck(): boolean {
  const { rbiOrderId, setRbiOrderId } = useCartContext();
  return isRbiOrderExpired(rbiOrderId, setRbiOrderId);
}

export const Payment: React.FC = () => {
  const { cartEntries, calculateCartTotal, calculateCartTotalWithDiscount } = useCart();
  const isOrderBayEnabled = useFlag(LaunchDarklyFlag.ENABLE_ORDER_BAY);
  const enableCashApp = useFlag(LaunchDarklyFlag.ENABLE_CASH_APP_PAYMENTS);
  const {
    serverOrder,
    serviceMode,
    shouldSkipPriceOrder,
    priceOrderSuccess,
    // TODO: BKPE-1956 - unavailableCartEntries
    unavailableCartEntries,
    priceMutationError,
    tipAmount,
    orderStatus,
    shouldShowTipPercentage,
    setIsTipLocked,
  } = useCartContext();

  const newPaymentValueIdRef = useRef('');
  const [isDefaultPaymentMethodSelected, setIsDefaultPaymentMethodSelected] = useState(true);
  const [isReloadDrawer, setIsReloadDrawer] = useState(false);
  const isPaymentRedesignEnabled = usePaymentCheckoutRedesignExperiment();

  const isDesktopWeb = useIsDesktopWebBreakpoint();
  const { navigate } = useNavigation();
  const router = useRouter();

  const { formatMessage } = useIntl();
  const isDeliveryOrder = isDelivery(serviceMode);
  const pathname = usePathname();
  const { store } = useStoreContext();
  const { isServiceModeAvailable } = useStoreAvailabilityFns();

  const {
    addPaymentMethod,
    canUseApplePay,
    canUseGooglePay,
    checkoutPaymentMethodId,
    isFirstData,
    isOrbital,
    setCheckoutPaymentMethodId,
    setEncryptionResult,
    storePaymentDetails,
    clearPaymentDetails,
    transformPaymentValues,
    getPrepaidPaymentMethod,
    isCheckoutErrorsModalOpen,
    processingPayment,
    setIsCheckoutErrorsModalOpen,
    setProcessingPayment,
    setOrderGrandTotal,
  } = usePaymentContext();

  const hasGiftCard = !!getPrepaidPaymentMethod();
  const [hasAddPaymentMethodError, setAddPaymentMethodError] = useState(false);
  const { loyaltyUser } = useLoyaltyUser();
  const isCartPayment = pathname === routes.cartPayment;
  const { evaluateLoyaltyUserIncentives } = useLoyaltyContext();
  const appliedRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const appliedLoyaltyOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const evaluatingIncentives = useAppSelector(selectors.loyalty.selectOffersLoading);
  const {
    cardIsValid,
    commitOrder,
    confirmStoreOnCheckout,
    ErrorDialog,
    handlePaymentMethodSelected,
    enableDelivery,
    errors,
    isCashPayment,
    isErrorModalOpen,
    isProcessingPayment,
    onChange,
    isValidReloadPaymentMethodWithCard,
    isValidTip,
    needReloadAgain,
    openServiceModeUnavailableErrorDialog,
    paymentMethods,
    paymentValues,
    placeOrderAriaLabel,
    reloadAmount,
    reloadPaymentValues,
    ServiceModeUnavailableErrorDialog,
    setReloadPaymentValues,
    showAddPaymentMethod: isAddNewCardMethodSelected,
    showReloadPaymentMethods,
    validateCreditCardForm,
    isAddGiftCardMethodSelected,
    setShowAddPaymentMethod,
    showPaymentMethodSelection,
    showPrepaidTransferBalance,
    setIsAddGiftCardMethodSelected,
  } = useOrderPayment();

  const { selectedReloadPaymentMethod } = usePrepaidsPaymentMethods();

  // always close the drawer when coming back from a different payment modal
  useFocusEffect(
    useCallback(() => {
      if (isPaymentRedesignEnabled) {
        setIsOpenPaymentDrawer(false);
      }
    }, [isPaymentRedesignEnabled])
  );

  const isRbiOrderIdValid = useRbiOrderIdExpirationCheck();
  useEffect(() => {
    if (!isRbiOrderIdValid) {
      navigate(routes.cart, { replace: true });
    }
  }, [isRbiOrderIdValid, navigate]);

  const [isEncryptionActive, setIsEncryptionActive] = useState(false);
  const [isOpenPaymentDrawer, setIsOpenPaymentDrawer] = useState(false);
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;
  const enableGiftCard = useFlag(LaunchDarklyFlag.ENABLE_GIFT_CARD);
  const checkoutDeliveryPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM);
  const rawSupportedCardTypes: SupportedCardType[] =
    useFlag(LaunchDarklyFlag.SUPPORTED_CARD_BRANDS_VARIATIONS) || defaultSupportedCardTypes;
  const enableOnetimePaymentAfterVault = useFlag(
    LaunchDarklyFlag.ENABLE_VAULT_AFTER_MAKING_ONE_TIME_PAYMENT
  );
  const enableCheckoutAnimation = useFlag(LaunchDarklyFlag.ENABLE_CHECKOUT_ANIMATION);
  const supportedCardTypes = rawSupportedCardTypes
    .map(cardType => mapPaymentMethodCardTypes(cardType))
    .filter(Boolean) as CardType[];
  const orderInjectFlow = useFlag(LaunchDarklyFlag.ORDER_INJECTION_FLOW);

  const showOrderProcessingDisclaimer = orderInjectFlow === OrderInjectionFlowVariations.IM_HERE;

  const nativePaymentDisclaimer = getNativePaymentDisclaimer(
    canUseApplePay,
    canUseGooglePay,
    formatMessage
  );
  const isSignUpAfterCart = useIsSignUpAfterCart();
  const checkoutPriceMinimum = isDeliveryOrder ? checkoutDeliveryPriceMinimum : 0;
  const { region } = useLocale();
  const isCARegion = region === Region.CA;

  const cartSubTotal =
    isSignUpAfterCart || shouldSkipPriceOrder
      ? calculateCartTotal()
      : Number(serverOrder?.cart?.subTotalCents);
  const cartPriceTooLow = cartSubTotal < checkoutPriceMinimum;

  const { getErrorMessages, getLoyaltyPaymentErrorMessages } = useGetPaymentErrors({
    cartPriceTooLow,
  });

  const pricedAndAvailable =
    priceOrderSuccess && !unavailableCartEntries.length && !priceMutationError;
  const paymentErrorMessages = getErrorMessages({
    invalidOfferError: false,
    invalidPaymentMethodError:
      !cardIsValid || !isValidReloadPaymentMethodWithCard || hasAddPaymentMethodError,
    invalidTipAmountError: !isValidTip,
    pricedAndAvailableError: !pricedAndAvailable,
  });

  const queryParams = getQueryParams();
  const {
    subTotalCents = 0,
    serverOrderTip,
    additionalDiscountAmount,
    cart,
  } = useTotalTipCalculations({ serverOrder, serviceMode });

  const handleAddPaymentMethod = useCallback(
    async (encryptionResult?: IEncryptionResult) => {
      try {
        if (isOrbital && !encryptionResult) {
          throw new Error('Orbital encryption results not available');
        }
        const newPaymentValueId = await addPaymentMethod(
          transformPaymentValues({
            paymentValues,
            paymentFieldVariations,
          }),
          {
            skipErrorDialogOnError: true,
          },
          encryptionResult
        );
        setCheckoutPaymentMethodId(newPaymentValueId);
        handlePaymentMethodSelected(newPaymentValueId);
        return newPaymentValueId;
      } catch (error) {
        setAddPaymentMethodError(true);
        setIsCheckoutErrorsModalOpen(true);
        return null;
      }
    },
    [
      addPaymentMethod,
      isOrbital,
      handlePaymentMethodSelected,
      paymentFieldVariations,
      paymentValues,
      setCheckoutPaymentMethodId,
      setIsCheckoutErrorsModalOpen,
      transformPaymentValues,
    ]
  );

  const loyaltyPaymentMethodErrors = getLoyaltyPaymentErrorMessages();
  const errorMessages = useMemo(() => {
    return [...paymentErrorMessages, ...loyaltyPaymentMethodErrors];
  }, [paymentErrorMessages, loyaltyPaymentMethodErrors]);

  const { isCartTotalNegative = false } = calculateCartTotalWithDiscount?.() || {};
  const positiveCartTotal = isCartTotalNegative
    ? 0
    : getServerOrderSubtotalBeforeOfferDiscounts(serverOrder);

  const handleOnSubmit = useCallback(
    async (event?: GestureResponderEvent, encryptionResult?: IEncryptionResult) => {
      if (confirmStoreOnCheckout && !isDeliveryOrder) {
        logCartContinueOrder();
      }
      if (event) {
        event.preventDefault();
      }

      if (isAddNewCardMethodSelected && !validateCreditCardForm(true)) {
        return;
      }

      if (
        isAddNewCardMethodSelected &&
        validateCreditCardForm() &&
        !checkoutPaymentMethodId &&
        paymentValues.saveCard
      ) {
        const skipVaultStep = enableOnetimePaymentAfterVault && isFirstData;
        if (!skipVaultStep) {
          const paymentId = await handleAddPaymentMethod(encryptionResult);

          if (!paymentId) {
            return;
          }

          newPaymentValueIdRef.current = paymentId;
        }
      }
      const loyaltyId = loyaltyUser?.id;
      if (loyaltyId && isAddNewCardMethodSelected && appliedLoyaltyOffers.length) {
        const paymentMethod = mapCardTypeToLoyalty(paymentValues.cardType);

        await evaluateLoyaltyUserIncentives(
          loyaltyId,
          appliedLoyaltyOffers,
          cartEntries,
          appliedRewards,
          positiveCartTotal,
          paymentMethod
        );
      }

      const serviceModeIsNotAvailableOrIsDisabled = !isServiceModeAvailable(
        store,
        serviceMode,
        getStoreLocalTime()
      );

      if (serviceModeIsNotAvailableOrIsDisabled) {
        openServiceModeUnavailableErrorDialog({
          message: formatMessage(
            { id: 'currentServiceModeUnavailable' },
            {
              // TODO: BKPE-6812 ORDER_BAY_PILOT. Remove or consolidate this logic once the pilot is done
              serviceMode: getLocalizedServiceMode(serviceMode, formatMessage, isOrderBayEnabled),
            }
          ),
        });
        return;
      }

      if (errorMessages.length > 0) {
        setIsCheckoutErrorsModalOpen(true);
      } else if (!isDeliveryOrder && confirmStoreOnCheckout) {
        if (!enableOnetimePaymentAfterVault || !isFirstData) {
          // If it is a one time payment with a newly added card, we want to store the info to place the order (in case of Curbside or page refresh).
          // Otherwise we clear the localStorage, because `paymentValues.saveCard` can be false due to stale data (see BKPE-256).
          if (!paymentValues.saveCard) {
            if (isAddNewCardMethodSelected) {
              storePaymentDetails({ state: paymentValues });
            } else {
              clearPaymentDetails();
            }
          }
        } else {
          // skipping standalone vault step for first data
          if (isAddNewCardMethodSelected) {
            storePaymentDetails({ state: paymentValues });
          } else {
            clearPaymentDetails();
          }
        }

        // navigation
        router.navigate(routes.cartStoreConfirmation);
      } else {
        commitOrder();
        setProcessingPayment(true);
      }
    },
    [
      confirmStoreOnCheckout,
      isDeliveryOrder,
      isAddNewCardMethodSelected,
      validateCreditCardForm,
      checkoutPaymentMethodId,
      paymentValues,
      loyaltyUser?.id,
      appliedLoyaltyOffers,
      isServiceModeAvailable,
      store,
      serviceMode,
      errorMessages.length,
      enableOnetimePaymentAfterVault,
      isFirstData,
      handleAddPaymentMethod,
      evaluateLoyaltyUserIncentives,
      cartEntries,
      appliedRewards,
      positiveCartTotal,
      openServiceModeUnavailableErrorDialog,
      formatMessage,
      isOrderBayEnabled,
      setIsCheckoutErrorsModalOpen,
      router,
      storePaymentDetails,
      clearPaymentDetails,
      commitOrder,
      setProcessingPayment,
    ]
  );

  const handlePayPal = useCallback(
    ({ nonce, deviceData, details }: IBraintreeResult) => {
      setCheckoutPaymentMethodId(CartPaymentType.PAYPAL);
      handlePaymentMethodSelected(CartPaymentType.PAYPAL);
      storePaymentDetails({
        accountIdentifier: CartPaymentType.PAYPAL,
        paypalDetails: {
          nonce,
          deviceData,
          details,
        },
      });
      handleOnSubmit();
    },
    [handleOnSubmit, handlePaymentMethodSelected, setCheckoutPaymentMethodId, storePaymentDetails]
  );

  const handlePayPalError = useCallback(
    (error: Error) => {
      logger.error({ error, message: 'PayPal error' });
      if (checkoutPaymentMethodId === CartPaymentType.PAYPAL) {
        setIsCheckoutErrorsModalOpen(true);
      }
    },
    [checkoutPaymentMethodId]
  );

  const handleVenmo = useCallback(
    ({ nonce, deviceData, details }: IBraintreeResult) => {
      setCheckoutPaymentMethodId(CartPaymentType.VENMO);
      handlePaymentMethodSelected(CartPaymentType.VENMO);
      storePaymentDetails({
        accountIdentifier: CartPaymentType.VENMO,
        venmoDetails: {
          details,
          deviceData,
          nonce,
        },
      });
      handleOnSubmit();
    },
    [handleOnSubmit, handlePaymentMethodSelected, setCheckoutPaymentMethodId, storePaymentDetails]
  );

  const handleVenmoError = useCallback((error: Error) => {
    logger.error({ error, message: 'Venmo error' });
    setIsCheckoutErrorsModalOpen(true);
  }, []);

  const handleCashApp = useCallback(
    (result: ICashAppResult) => {
      setCheckoutPaymentMethodId(CartPaymentType.CASHAPP);
      handlePaymentMethodSelected(CartPaymentType.CASHAPP);
      storePaymentDetails({
        accountIdentifier: CartPaymentType.CASHAPP,
        cashappDetails: result,
      });
      removeAllQueryParams();
      handleOnSubmit();
    },
    [handleOnSubmit, handlePaymentMethodSelected, setCheckoutPaymentMethodId, storePaymentDetails]
  );

  const handleCashAppError = useCallback((error: any) => {
    logger.error({ orderId: serverOrder?.rbiOrderId, error, message: 'Cash App error' });
    setIsCheckoutErrorsModalOpen(true);
  }, []);

  const handleCashAppRedirection = useCallback(() => {
    if (checkoutPaymentMethodId !== CartPaymentCardType.CASHAPP) {
      setCheckoutPaymentMethodId(CartPaymentType.CASHAPP);
      handlePaymentMethodSelected(CartPaymentType.CASHAPP);
    }
  }, [checkoutPaymentMethodId, handlePaymentMethodSelected, setCheckoutPaymentMethodId]);

  const selectedMethod = paymentMethods.find(
    method => method.accountIdentifier === checkoutPaymentMethodId
  );

  const currentSelectedMethodType = getCurrentSelectedMethodType({
    isAddGiftCardMethodSelected,
    isAddNewCardMethodSelected,
    selectedMethodExist: !!selectedMethod,
  });
  const isAddNewPaymentMethodTypeSelected =
    currentSelectedMethodType === CurrentSelectedMethodType.ADD_NEW_PAYMENT_METHOD;

  const isPlaceOrderButtonDisabled =
    isProcessingPayment ||
    (!!selectedMethod?.prepaid && !enableGiftCard) ||
    isAddNewPaymentMethodTypeSelected ||
    (showReloadPaymentMethods && !!paymentMethods.length && !reloadPaymentValues.saveCard);

  // Check if PayPal and Venmo are available
  const isPayPalAvailable = paymentMethods.some(method => isPayPal(method.accountIdentifier));
  const isVenmoAvailable = paymentMethods.some(method => isVenmo(method.accountIdentifier));

  useEffect(() => {
    const { totalCents = 0 } = cart || {};
    const grandTotal = Math.max(totalCents - Number(serverOrderTip) + tipAmount, 0);
    if (!isProcessingPayment) {
      // Setting grand total and checking payments
      setOrderGrandTotal(grandTotal);
    }
  }, [
    cart,
    serverOrderTip,
    tipAmount,
    checkoutPaymentMethodId,
    isProcessingPayment,
    setOrderGrandTotal,
    isCartPayment,
  ]);

  const handleOnPress = useCallback(() => {
    if (isOrbital && !selectedMethod?.accountIdentifier) {
      return setIsEncryptionActive(true);
    }
    const accountIdentifier =
      selectedMethod?.accountIdentifier ?? selectedMethod?.fdAccountId ?? '';
    const revaultInformation: IRevaultInformation = shouldReVault({
      accountIdentifier,
      isOrbitalPaymentEnabled: isOrbital,
      methods: paymentMethods,
    });

    const isRevaultRequired = revaultInformation.isExpired || revaultInformation.isInvalid;

    if (isRevaultRequired) {
      router.navigate(
        `${routes.addCard}?accountToDelete=${selectedMethod?.accountIdentifier}&isInCart=true`
      );
    }
    handleOnSubmit();
  }, [
    handleOnSubmit,
    isOrbital,
    paymentMethods,
    router,
    selectedMethod?.accountIdentifier,
    selectedMethod?.fdAccountId,
  ]);

  const onEncryptionResult = useCallback(
    (data: IEncryptionResult) => {
      setIsEncryptionActive(false);
      setEncryptionResult(data);
      handleOnSubmit(undefined, data);
    },
    [handleOnSubmit, setEncryptionResult]
  );

  // Handle encryption error
  const onEncryptionError = (error: IEncryptionError) => {
    setIsEncryptionActive(false);
    setIsCheckoutErrorsModalOpen(true);
    const errorMessage = error.error;
    logger.error({ error: new Error(errorMessage), message: errorMessage });
  };

  /**
   * Following the normal flow, serverOrder should always be fulfilled on this screen.
   * But we identified the following cases when this is not ensured:
   * - Navigating backwards from OrderConfirmation page clicking the back button.
   * - User manually changing the URL.
   * - User navigated to support from this screen and then closed the support modal.
   *
   * For these edge cases is just safer to redirect to the main Cart,
   * where the tryPricing is always executed and the ServerOrder is properly fetched.
   */
  useEffect(() => {
    if (!serverOrder && !isProcessingPayment) {
      navigate(routes.cart, { replace: true });
    }
  }, [serverOrder, isProcessingPayment, navigate]);

  // Logs the Payment Method Selected Event
  useEffect(() => {
    if (selectedMethod) {
      if (isDefaultPaymentMethodSelected) {
        setIsDefaultPaymentMethodSelected(false);
        return;
      }

      let paymentInfo: { paymentMethod: string; cardBrand?: string };
      const isCreditCard = isCreditCardPaymentMethod(selectedMethod);
      if (isCreditCard && selectedMethod.credit) {
        paymentInfo = {
          paymentMethod: CartPaymentCardType.CREDIT,
          cardBrand: selectedMethod.credit.cardType,
        };
      } else if (selectedMethod.prepaid) {
        paymentInfo = {
          paymentMethod: CartPaymentCardType.GIFT_CARD,
        };
      } else {
        paymentInfo = {
          paymentMethod: selectedMethod.accountIdentifier || '',
        };
      }

      logRBIEvent(
        {
          name: CustomEventNames.PAYMENT_METHOD_SELECTED,
          type: EventTypes.Other,
          attributes: paymentInfo,
        },
        { skipLoggingToBraze: true }
      );
    }
  }, [selectedMethod]);

  // Check cashapp redirection for mobile web
  useEffect(() => {
    if (queryParams && (queryParams as any)?.isRedirected === 'true') {
      handleCashAppRedirection();
    }
  }, [checkoutPaymentMethodId, handleCashAppRedirection, queryParams]);

  // Remove one time payment method (is selected) on mount and unmount
  useEffect(() => {
    const clearOneTimePaymentMethod = () => {
      if (isPaymentRedesignEnabled) {
        clearPaymentDetails();
      }
    };

    clearOneTimePaymentMethod();

    return () => {
      // For curbside, we need to preserve this data to continue with the "I'm Here" flow
      if (serviceMode !== ServiceMode.CURBSIDE) {
        clearOneTimePaymentMethod();
      }
    };
  }, []);

  if (enableCheckoutAnimation && processingPayment && !isErrorModalOpen) {
    return <PaymentProcessLoading rbiOrderId={serverOrder?.rbiOrderId} />;
  }

  const isLoading = isProcessingPayment || evaluatingIncentives;

  return (
    <View
      testID="cart-payment-layout"
      style={{ flex: 1 }}
      onPointerDown={event => {
        // TODO not sure this does anything wasn't able to get it to work but preserving it here on expo-router refactor
        // Closing payment drawer if opening (only Desktop)
        // @ts-ignore tabIndex prop is not recognized
        if (isDesktopWeb && isOpenPaymentDrawer && event.target.tabIndex === 0) {
          setIsOpenPaymentDrawer(false);
        }
      }}
    >
      <CardModal
        headerText={formatMessage({ id: 'orderPayment' })}
        scrollView
        standardPadding
        dismissType="back"
        webAnimate={false}
        invertHeaderColor
        isFullScreen
        footer={isPaymentRedesignEnabled ? <PaymentFooter /> : undefined}
      >
        {isCheckoutErrorsModalOpen && (
          <CheckoutErrorsModal
            checkoutErrors={errorMessages}
            closeModal={() => {
              setIsCheckoutErrorsModalOpen(false);
              setAddPaymentMethodError(false);
            }}
          />
        )}
        {!isPaymentRedesignEnabled && (
          <SectionHeading accessible accessibilityRole="header">
            {formatMessage({ id: 'paymentDetails' })}
          </SectionHeading>
        )}
        {/*TODO replace with auth.user && auth.user.fdCustomerId*/}
        {/* Todo create a separate component to display the selected method */}
        <PaymentSelection>
          <>
            {!isPaymentRedesignEnabled ? (
              <>
                {showPaymentMethodSelection && (
                  <PaymentMethod
                    aria-expanded="true"
                    methods={paymentMethods}
                    selectedMethodId={checkoutPaymentMethodId}
                    handleSelect={handlePaymentMethodSelected}
                    isAddCardOpen={isAddNewCardMethodSelected}
                    openAddCardOnPress={setShowAddPaymentMethod}
                    isAddGiftCardMethodSelected={isAddGiftCardMethodSelected}
                    setIsAddGiftCardMethodSelected={setIsAddGiftCardMethodSelected}
                    isOrbitalPaymentEnabled={isOrbital}
                  />
                )}

                {isAddNewCardMethodSelected && (
                  <CreditCardFormInputs
                    ariaLabel="credit-card-form-inputs"
                    isDelivery={isDeliveryOrder}
                    onChange={onChange}
                    paymentValues={paymentValues}
                    validateCreditCardForm={validateCreditCardForm}
                    errors={errors}
                    withBorder={false}
                    withPadding={false}
                    supportedCardTypes={supportedCardTypes}
                  />
                )}
              </>
            ) : (
              <PaymentMethodsContainer>
                <PaymentRedesign
                  methods={paymentMethods}
                  selectedMethodId={checkoutPaymentMethodId}
                  reloadAmount={reloadAmount}
                  reloadPaymentValues={reloadPaymentValues}
                  setReloadPaymentValues={setReloadPaymentValues}
                  isDelivery={isDeliveryOrder}
                  showReloadPaymentMethods={showReloadPaymentMethods}
                  onChange={() => {
                    setIsOpenPaymentDrawer(true);
                    setIsReloadDrawer(false);
                  }}
                  onChangeReload={() => {
                    setIsOpenPaymentDrawer(true);
                    setIsReloadDrawer(true);
                  }}
                />
                <AvailablePayments>
                  {!isCARegion && (
                    <IconContainer>
                      <Icon variant="smallGiftCardWhite" size="md" />
                    </IconContainer>
                  )}
                  <IconContainer>
                    {cardIconRenderer({
                      methodType: CardTypes.CREDIT,
                      isMediumSize: true,
                      isNoBorder: true,
                      showGenericIcon: true,
                    })}
                  </IconContainer>
                  {isPayPalAvailable && !showReloadPaymentMethods && !isCARegion && (
                    <IconContainer>
                      {cardIconRenderer({
                        methodType: CardTypes.PAYPAL,
                        isMediumSize: true,
                        isNoBorder: true,
                        showGenericIcon: false,
                      })}
                    </IconContainer>
                  )}
                  {isVenmoAvailable && !showReloadPaymentMethods && !isCARegion && (
                    <IconContainer>
                      {cardIconRenderer({
                        methodType: CardTypes.VENMO,
                        isMediumSize: true,
                        isNoBorder: true,
                        showGenericIcon: false,
                      })}
                    </IconContainer>
                  )}
                  {canUseApplePay && (
                    <IconContainer>
                      {cardIconRenderer({
                        methodType: CardTypes.APPLE_PAY,
                        isMediumSize: true,
                        isNoBorder: true,
                        showGenericIcon: false,
                      })}
                    </IconContainer>
                  )}
                  {canUseGooglePay && (
                    <IconContainer>
                      {cardIconRenderer({
                        methodType: CardTypes.GOOGLE_PAY,
                        isMediumSize: true,
                        isNoBorder: true,
                        showGenericIcon: false,
                      })}
                    </IconContainer>
                  )}
                  {enableCashApp && !showReloadPaymentMethods && !isCARegion && (
                    <IconContainer>
                      {cardIconRenderer({
                        methodType: CardTypes.CASHAPP,
                        isMediumSize: true,
                        isNoBorder: true,
                        showGenericIcon: false,
                      })}
                    </IconContainer>
                  )}
                </AvailablePayments>
              </PaymentMethodsContainer>
            )}
            {!isPaymentRedesignEnabled && showPrepaidTransferBalance && <PrepaidTransferBalance />}
            {!isPaymentRedesignEnabled && showReloadPaymentMethods && !!paymentMethods.length && (
              <PrepaidMethodReload
                reloadAmount={reloadAmount}
                reloadPaymentValues={reloadPaymentValues}
                setReloadPaymentValues={setReloadPaymentValues}
                isDelivery={isDeliveryOrder}
              />
            )}
            {needReloadAgain && (
              <ErrorContainer>
                <ErrorMessage>
                  {formatMessage({ id: 'insufficientBalanceReloadAgain' })}
                </ErrorMessage>
              </ErrorContainer>
            )}
            {isPaymentRedesignEnabled ? (
              <>
                {isDeliveryOrder && (
                  <TipSelectorRedesign
                    subTotalCents={subTotalCents}
                    otherDiscountAmount={additionalDiscountAmount}
                    showTipPercentage={shouldShowTipPercentage(
                      subTotalCents + additionalDiscountAmount
                    )}
                    isDisabled={isPlaceOrderButtonDisabled}
                  />
                )}
                <Totals
                  // @ts-expect-error TS(2322) FIXME: Type '{ orderStatus: OrderStatus | null; serverOrd... Remove this comment to see the full error message
                  orderStatus={orderStatus}
                  serverOrder={serverOrder}
                  serviceMode={serviceMode}
                  shouldSkipPriceOrder={shouldSkipPriceOrder}
                />
              </>
            ) : (
              <OrderPaymentTotals
                serverOrder={serverOrder}
                serviceMode={serviceMode}
                isDisabled={isPlaceOrderButtonDisabled}
              />
            )}

            {showOrderProcessingDisclaimer && (
              <DisclaimerWrapper>
                {formatMessage({
                  id: getOrderProcessingText({
                    isCashPayment,
                  }),
                })}
              </DisclaimerWrapper>
            )}
            {!selectedMethod && nativePaymentDisclaimer && (
              <WarningContainer>
                <WarningMessage message={nativePaymentDisclaimer} />
              </WarningContainer>
            )}

            {!(
              selectedMethod &&
              (isPayPal(selectedMethod.accountIdentifier) ||
                isVenmo(selectedMethod.accountIdentifier) ||
                isCashApp(selectedMethod?.accountIdentifier))
            ) &&
              !isPaymentRedesignEnabled && (
                <>
                  <PlaceOrderButton
                    confirmation={!confirmStoreOnCheckout || isDeliveryOrder}
                    isLoading={isLoading}
                    disabled={isPlaceOrderButtonDisabled || isProcessingPayment}
                    placeOrderAriaLabel={placeOrderAriaLabel}
                    onPress={handleOnPress}
                  />
                  {!isAddNewCardMethodSelected && !isCashApp(selectedMethod?.accountIdentifier) && (
                    <PayPalVenmoExtraDisplay
                      serverOrder={serverOrder}
                      onPayPalResult={handlePayPal}
                      onPayPalError={handlePayPalError}
                      onVenmoResult={handleVenmo}
                      onVenmoError={handleVenmoError}
                      isPayPalAvailable={isPayPalAvailable}
                      isVenmoAvailable={isVenmoAvailable}
                    />
                  )}
                </>
              )}

            {selectedMethod &&
            isPayPal(selectedMethod.accountIdentifier) &&
            !isPaymentRedesignEnabled ? (
              <PayPalButton
                serverOrder={serverOrder}
                onResult={handlePayPal}
                onError={handlePayPalError}
                showLoading={isProcessingPayment || evaluatingIncentives}
              ></PayPalButton>
            ) : null}

            {selectedMethod &&
            isVenmo(selectedMethod.accountIdentifier) &&
            !isPaymentRedesignEnabled ? (
              <VenmoButton
                serverOrder={serverOrder}
                onResult={handleVenmo}
                onError={handleVenmoError}
                showLoading={isLoading}
              ></VenmoButton>
            ) : null}

            {selectedMethod &&
            isCashApp(selectedMethod.accountIdentifier) &&
            !isPaymentRedesignEnabled ? (
              <CashAppButton
                serverOrder={serverOrder}
                onResult={handleCashApp}
                onError={handleCashAppError}
                showLoading={isProcessingPayment || evaluatingIncentives}
                tipAmount={tipAmount}
                setIsTipLocked={setIsTipLocked}
              ></CashAppButton>
            ) : null}

            {isDeliveryOrder && enableDelivery && !isPaymentRedesignEnabled && (
              <DeliveryCancellationPolicy />
            )}
            {isPaymentRedesignEnabled && <DeliveryCancellationRedesign />}
          </>
        </PaymentSelection>
        <PaymentMethodSelector
          isOpen={isOpenPaymentDrawer}
          onClose={() => setIsOpenPaymentDrawer(false)}
          onConfirm={() => setIsOpenPaymentDrawer(false)}
          setIsGiftCardVisible={() =>
            router.navigate(hasGiftCard ? routes.transferGiftCard : routes.addGiftCard)
          }
          methods={paymentMethods}
          selectedMethodId={checkoutPaymentMethodId}
          reloadSelectedMethodId={
            isReloadDrawer ? selectedReloadPaymentMethod?.fdAccountId || '' : ''
          }
          isReloadDrawer={isReloadDrawer}
        />

        <ErrorDialog />
        <ServiceModeUnavailableErrorDialog testID="servicemode-unavailable-error-modal" />
        {!isPaymentRedesignEnabled && isOrbital && isEncryptionActive ? (
          <SafetechEncryption
            cardNumber={paymentValues.cardNumber}
            cvv={paymentValues.cvv || ''}
            onResult={onEncryptionResult}
            onError={onEncryptionError}
          />
        ) : null}
      </CardModal>
    </View>
  );
};
